import { zodResolver } from '@hookform/resolvers/zod';
import {
  ITaxConnectionFormSchema,
  LegalEntityApplicationScope,
  TaxConnectionFormSchema,
  TaxConnectionSchema,
  TaxIdentifierEnum,
  TaxProviderTypeEnum,
  TaxStatusEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../../api/axios';
import { useCreateEntity } from '../../../../../../api/queryUtils';
import {
  doTestTaxConnection,
  settingsQueryKeys,
} from '../../../../../../api/settingsService';
import {
  MAlert,
  MButton,
  MCustomRadioGroup,
  MDivider,
  MFlex,
  MFormField,
  MHStack,
  MInput,
} from '../../../../../../components/Monetize';
import { LegalEntityDropdownMultiSelect } from '../../../../../../components/Monetize/Dropdowns/LegalEntityDropdownMultiSelect';
import { MCustomSelect } from '../../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import {
  TAX_IDENTIFIER_SELECT_OPTIONS,
  TAX_LEGAL_ENTITY_SCOPE_OPTIONS,
  VERTEX_CREDENTIAL_TYPES,
  VERTEX_IDENTITY_PROVIDERS,
} from '../../../../../../constants/taxes';
import { logger } from '../../../../../../services/logger';

interface VertexFormProps {
  connection?: Maybe<ITaxConnectionFormSchema>;
  handleCancel: () => void;
}

export const VertexForm = ({ connection, handleCancel }: VertexFormProps) => {
  const queryClient = useQueryClient();
  const [isStatusLoading, setStatusLoading] = useState<boolean>(false);
  const [testStatus, setTestStatus] = useState<{
    provider: TaxProviderTypeEnum;
    type?: Maybe<string>;
    identityProvider?: Maybe<string>;
    baseUrl?: Maybe<string>;
    clientId?: Maybe<string>;
    clientSecret?: Maybe<string>;
    companyCode?: Maybe<string>;
  } | null>(() =>
    connection?.credentials
      ? {
          provider: TaxProviderTypeEnum.VERTEX,
          type: connection.credentials.type,
          identityProvider: connection.credentials.identityProvider,
          baseUrl: connection.credentials.baseUrl,
          clientId: connection.credentials.clientId,
          clientSecret: connection.credentials.clientSecret,
          companyCode: connection.credentials.companyCode,
        }
      : null,
  );
  const [testStatusError, setTestStatusError] = useState<Error | null>(null);

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ITaxConnectionFormSchema>({
    resolver: zodResolver(TaxConnectionFormSchema),
    mode: 'onSubmit',
    values: connection ? connection : undefined,
    defaultValues: {
      provider: TaxProviderTypeEnum.VERTEX,
      configurations: {
        identifier: TaxIdentifierEnum.TAX_ITEM_CODE,
        legalEntityOverride: LegalEntityApplicationScope.ALL,
        legalEntities: [],
      },
      credentials: {
        type: 'OSeriesCloudCredentials',
        identityProvider: 'LEGACY',
      },
      status: TaxStatusEnum.ACTIVE,
    },
  });

  const isValid = Object.keys(errors).length === 0;

  const { mutateAsync: createTaxConfiguration, isPending: isLoading } =
    useCreateEntity('tax', {
      onSuccess: (data) => {
        queryClient.setQueryData(
          [...settingsQueryKeys.taxByProvider(TaxProviderTypeEnum.VERTEX)],
          data,
        );
      },
      onError: (error) => {
        handleApiErrorToast(error);
      },
    });

  const watchProvider = watch('provider');
  const watchType = watch('credentials.type');
  const watchIdentityProvider = watch('credentials.identityProvider');
  const watchBaseUrl = watch('credentials.baseUrl');
  const watchClientId = watch('credentials.clientId');
  const watchClientSecret = watch('credentials.clientSecret');
  const watchCompanyCode = watch('credentials.companyCode');
  const watchLegalEntityOverride = watch('configurations.legalEntityOverride');

  const isTestStatusValid =
    !testStatusError &&
    testStatus &&
    testStatus.provider === watchProvider &&
    testStatus.type === watchType &&
    testStatus.identityProvider === watchIdentityProvider &&
    testStatus.baseUrl === watchBaseUrl &&
    testStatus.clientId === watchClientId &&
    testStatus.clientSecret === watchClientSecret &&
    testStatus.companyCode === watchCompanyCode;

  const isEmptyRequiredFields =
    !watchType ||
    !watchClientId ||
    !watchClientSecret ||
    !watchCompanyCode ||
    (watchType === 'OSeriesCloudCredentials' && !watchIdentityProvider) ||
    (watchType === 'OnDemandCredentials' && !watchBaseUrl);

  const onSubmitHandler = async (data: ITaxConnectionFormSchema) => {
    try {
      const transformedData = TaxConnectionSchema.parse(data);
      if (
        transformedData.configurations.legalEntityOverride !==
        LegalEntityApplicationScope.SCOPED
      ) {
        transformedData.configurations.legalEntities = [];
      }

      await createTaxConfiguration(transformedData);
      handleCancel();
    } catch (error) {
      logger.error('Error while saving tax configuration', error);
    }
  };

  const onTestConnection = async () => {
    try {
      setStatusLoading(true);
      const { provider, credentials } = getValues();

      if (
        !credentials?.type ||
        !credentials?.clientId ||
        !credentials?.clientSecret ||
        !credentials?.companyCode
      ) {
        return;
      }

      const testCredentials: {
        type: string;
        clientId: string;
        clientSecret: string;
        companyCode: string;
        identityProvider?: string;
        baseUrl?: string;
      } = {
        type: credentials.type,
        clientId: credentials.clientId,
        clientSecret: credentials.clientSecret,
        companyCode: credentials.companyCode,
      };

      if (
        credentials.type === 'OSeriesCloudCredentials' &&
        credentials.identityProvider
      ) {
        testCredentials.identityProvider = credentials.identityProvider;
      }

      if (credentials.type === 'OnDemandCredentials' && credentials.baseUrl) {
        testCredentials.baseUrl = credentials.baseUrl;
      }

      await doTestTaxConnection({
        provider,
        credentials: testCredentials,
      });

      setTestStatus({
        provider,
        type: credentials?.type,
        identityProvider: credentials?.identityProvider,
        baseUrl: credentials?.baseUrl,
        clientId: credentials?.clientId,
        clientSecret: credentials?.clientSecret,
        companyCode: credentials?.companyCode,
      });

      setTestStatusError(null);
    } catch (error) {
      logger.error('Error while testing tax connection', error);
      setTestStatusError(error as Error);
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} style={{ width: '100%' }}>
      <MFlex flexDirection="column">
        <MFlex mb="4" gap="4">
          <MFormField
            error={errors.credentials?.type}
            label="Credential Type"
            isRequired
          >
            <Controller
              name="credentials.type"
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <MCustomSelect
                  placeholder="Select Credential Type"
                  items={VERTEX_CREDENTIAL_TYPES}
                  onChange={(value) => {
                    onChange(value);
                    if (testStatusError) {
                      setTestStatusError(null);
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </MFormField>

          {watchType === 'OSeriesCloudCredentials' && (
            <MFormField
              error={errors.credentials?.identityProvider}
              label="Identity Provider"
              isRequired
            >
              <Controller
                name="credentials.identityProvider"
                control={control}
                render={({ field: { onChange, ...rest } }) => (
                  <MCustomSelect
                    placeholder="Select Identity Provider"
                    items={VERTEX_IDENTITY_PROVIDERS}
                    onChange={(value) => {
                      onChange(value);
                      if (testStatusError) {
                        setTestStatusError(null);
                      }
                    }}
                    {...rest}
                  />
                )}
              />
            </MFormField>
          )}

          {watchType === 'OnDemandCredentials' && (
            <MFormField
              error={errors.credentials?.baseUrl}
              label="Base URL"
              isRequired
            >
              <Controller
                name="credentials.baseUrl"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <MInput
                    value={value || ''}
                    onChange={(newValue) => {
                      onChange(newValue);
                      if (testStatusError) {
                        setTestStatusError(null);
                      }
                    }}
                    placeholder="https://example.com"
                    {...rest}
                  />
                )}
              />
            </MFormField>
          )}
        </MFlex>

        <MFlex mb="4" gap="4">
          <MFormField
            error={errors.credentials?.clientId}
            label="Client ID"
            isRequired
          >
            <Controller
              name="credentials.clientId"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <MInput
                  value={value || ''}
                  onChange={(newValue) => {
                    onChange(newValue);
                    if (testStatusError) {
                      setTestStatusError(null);
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </MFormField>

          <MFormField
            error={errors.credentials?.clientSecret}
            label="Client Secret"
            isRequired
          >
            <Controller
              name="credentials.clientSecret"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <MInput
                  value={value || ''}
                  onChange={(newValue) => {
                    onChange(newValue);
                    if (testStatusError) {
                      setTestStatusError(null);
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </MFormField>
        </MFlex>

        <MFormField
          mb="4"
          error={errors.configurations?.identifier}
          label="Tax Calculation Field"
          isRequired
          tooltip="Choose which field on the product you would like us to send to Vertex as the product external id."
        >
          <Controller
            name="configurations.identifier"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <MCustomSelect
                placeholder="Select Tax Calculation Field"
                items={TAX_IDENTIFIER_SELECT_OPTIONS}
                onChange={(value) => {
                  onChange(value);
                  if (testStatusError) {
                    setTestStatusError(null);
                  }
                }}
                {...rest}
              />
            )}
          />
        </MFormField>

        <MFormField
          mb="4"
          error={errors.credentials?.companyCode}
          label="Taxpayer Code"
          isRequired
        >
          <Controller
            name="credentials.companyCode"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <MInput
                value={value || ''}
                onChange={(newValue) => {
                  onChange(newValue);
                  if (testStatusError) {
                    setTestStatusError(null);
                  }
                }}
                {...rest}
              />
            )}
          />
        </MFormField>

        <MFormField
          error={errors.configurations?.legalEntityOverride}
          label="Apply to Which Legal Entities"
          tooltip="Determines which Legal Entities will use this tax configuration."
        >
          <Controller
            name="configurations.legalEntityOverride"
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <MCustomRadioGroup
                value={value}
                my={1}
                itemTitle="title"
                itemValue="value"
                items={TAX_LEGAL_ENTITY_SCOPE_OPTIONS}
                stackProps={{ direction: 'row' }}
                onChange={(newValue) =>
                  onChange(newValue as LegalEntityApplicationScope)
                }
              />
            )}
          />
        </MFormField>

        {watchLegalEntityOverride === LegalEntityApplicationScope.SCOPED && (
          <MFormField
            mb="4"
            error={errors.configurations?.legalEntities}
            label="Legal Entities"
            tooltip="Determines which Legal Entities will use this tax configuration."
          >
            <Controller
              name="configurations.legalEntities"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <LegalEntityDropdownMultiSelect
                  value={value}
                  setValue={onChange}
                />
              )}
            />
          </MFormField>
        )}

        <MDivider my={4} />
        <MFlex justify="space-between" align="center">
          {!isTestStatusValid && !testStatusError && (
            <MButton
              type="submit"
              variant="tertiary"
              fontSize="xs"
              onClick={() => onTestConnection()}
              isLoading={isStatusLoading}
              isDisabled={!isValid || isStatusLoading || isEmptyRequiredFields}
            >
              Test Connection
            </MButton>
          )}

          {testStatus && isTestStatusValid && (
            <MAlert
              type="success"
              alertProps={{ px: 2, py: 1 }}
              alertIconProps={{ width: 4, height: 4 }}
              alertDescriptionProps={{ fontSize: 'sm' }}
            >
              Connection Valid
            </MAlert>
          )}
          {!!testStatusError && (
            <MAlert
              type="error"
              alertProps={{ px: 2, py: 1 }}
              alertIconProps={{ width: 4, height: 4 }}
              alertDescriptionProps={{ fontSize: 'sm' }}
            >
              Connection invalid
            </MAlert>
          )}
          <MHStack gap="4" justify="end" align="center">
            <MButton variant="cancel" onClick={handleCancel}>
              Cancel
            </MButton>
            <MButton
              type="submit"
              variant="primary"
              isLoading={isLoading}
              isDisabled={!isTestStatusValid || isLoading}
            >
              Save
            </MButton>
          </MHStack>
        </MFlex>
      </MFlex>
    </form>
  );
};
